/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * The input component for validate email
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @version 1.14.0
 * @since 1.2.0
 */

import { ChangeEvent, useEffect, useState } from 'react';

import * as Yup from 'yup';

import ICommonTranslation from '../../../modules/internationalization/types/common';
import { Input } from '../index';
import { EInputKind, IInputProps } from '../Input/Input';

interface IEmailInput {
    emailPlaceholder?: string;
    emailInputValue: string;
    isAllowEmpty?: boolean;
    isValid?: boolean;
    onEmailInputBlur?: (value: string) => void;
    onEmailInputChange?: (value: string) => void;
    onEmailInputValidate?: (value: boolean) => void;
    translation: ICommonTranslation;
}

export type TEmailInputProps = IEmailInput & IInputProps;

export const EmailInput = (props: TEmailInputProps) => {
    const {
        isAllowEmpty, isValid: propsIsValid, emailPlaceholder, emailInputValue, onEmailInputBlur,
        onEmailInputChange, onEmailInputValidate, translation, ...inputProps
    } = props;
    const [isValid, setValid] = useState(true);

    const handleValidate = (value: string) => {
        Yup.string().email().nullable(isAllowEmpty || undefined).isValid(value).then(isEmailValid => {
            setValid(isEmailValid);
            if (onEmailInputValidate) {
                onEmailInputValidate(isEmailValid)
            }
        });
    };

    useEffect(() => {
        if (propsIsValid !== undefined) setValid(propsIsValid);
    }, [propsIsValid]);

    useEffect(() => {
        if (emailInputValue) handleValidate(emailInputValue);
    }, [emailInputValue]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.trim();
        handleValidate(value);
        if (onEmailInputChange) onEmailInputChange(value);
    };

    const handleBlur = () => {
        handleValidate(emailInputValue);
        if (onEmailInputBlur) onEmailInputBlur(emailInputValue);
    };

    return (
        <Input
            {...inputProps}
            hintMessage={isValid ? undefined : props.translation.emailInput.hint}
            kind={isValid ? undefined : EInputKind.error}
            placeholder={emailPlaceholder ? emailPlaceholder : translation.emailInput.placeholder}
            required={!isAllowEmpty}
            value={emailInputValue}
            onBlur={handleBlur}
            onChange={handleChange}
        />
    )
};
